import React, { useEffect, useState } from 'react';
import { QuarterlyMonthlyToggle } from '../components/QuarterlyMonthlyToggle';
import { MonthlyBarChart } from '../components/MonthlyBarChart';
import { getCurrentQuarterIndex, getQuarterStatus, getMonthStatus, FISCAL_MONTH_ORDER, getMonthLabel } from '../utils/fiscalYear';
import api from '../services/api';

// Helper function to format currency
const formatCurrency = (value: number): string => {
  if (value >= 1000000) return `£${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `£${Math.round(value / 1000)}K`;
  return `£${value}`;
};

interface QuarterData {
  budget: number;
  forecast: number;
  actual: number;
}

interface MonthData {
  month: number;
  label: string;
  budget: number;
  forecast: number;
  actual: number;
}

interface Territory {
  id: string;
  name: string;
  repId: string;
  q1: QuarterData;
  q2: QuarterData;
  q3: QuarterData;
  q4: QuarterData;
  q1Months?: MonthData[];
  q2Months?: MonthData[];
  q3Months?: MonthData[];
  q4Months?: MonthData[];
}

interface TerritoryData {
  config: {
    companyName: string;
  };
  summary: {
    totalBudget: number;
    totalForecast: number;
    totalActual: number;
    territoryCount: number;
  };
  territories: Territory[];
  monthlyPerformance?: MonthData[];
}

export const TerritoryDashboard: React.FC = () => {
  const [data, setData] = useState<TerritoryData | null>(null);
  const [granularity, setGranularity] = useState<'quarterly' | 'monthly'>('quarterly');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get('/dashboard/territories', { params: { granularity } });
        if (response.data.success) {
          setData(response.data.data);
        } else {
          setError('Failed to load territory data');
        }
      } catch (err) {
        setError('Failed to load territory data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [granularity]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-danger text-lg">{error || 'No data available'}</p>
        </div>
      </div>
    );
  }

  // Get current quarter for determining past/current/future status
  const currentQuarter = getCurrentQuarterIndex();

  // Calculate quarterly totals
  const quarterlyData = [
    { quarter: 'Q1', budget: data.territories.reduce((sum, t) => sum + t.q1.budget, 0), forecast: data.territories.reduce((sum, t) => sum + t.q1.forecast, 0), actual: data.territories.reduce((sum, t) => sum + t.q1.actual, 0) },
    { quarter: 'Q2', budget: data.territories.reduce((sum, t) => sum + t.q2.budget, 0), forecast: data.territories.reduce((sum, t) => sum + t.q2.forecast, 0), actual: data.territories.reduce((sum, t) => sum + t.q2.actual, 0) },
    { quarter: 'Q3', budget: data.territories.reduce((sum, t) => sum + t.q3.budget, 0), forecast: data.territories.reduce((sum, t) => sum + t.q3.forecast, 0), actual: data.territories.reduce((sum, t) => sum + t.q3.actual, 0) },
    { quarter: 'Q4', budget: data.territories.reduce((sum, t) => sum + t.q4.budget, 0), forecast: data.territories.reduce((sum, t) => sum + t.q4.forecast, 0), actual: data.territories.reduce((sum, t) => sum + t.q4.actual, 0) },
  ];

  // Calculate totals with quarter-status awareness
  // Past quarters: count actual only (forecast is stale)
  // Current quarter: count actual + forecast
  // Future quarters: count forecast only
  const totalBudget = quarterlyData.reduce((sum, q) => sum + q.budget, 0);

  let totalActual = 0;
  let totalForecast = 0;

  quarterlyData.forEach((q, index) => {
    const status = getQuarterStatus(index, currentQuarter);

    if (status === 'past') {
      // Past: only count actual (forecast is no longer relevant)
      totalActual += q.actual || 0;
    } else if (status === 'current') {
      // Current: count both actual and remaining forecast
      totalActual += q.actual || 0;
      totalForecast += q.forecast || 0;
    } else {
      // Future: only count forecast (no actual yet)
      totalForecast += q.forecast || 0;
    }
  });

  const totalProgress = totalActual + totalForecast;
  const isOnTrack = totalProgress >= totalBudget * 0.95;

  // Calculate bar widths for horizontal chart
  const maxValue = Math.max(totalBudget, totalProgress, 1);
  const budgetWidth = (totalBudget / maxValue) * 100;
  const actualWidth = (totalActual / maxValue) * 100;
  const forecastWidth = (totalForecast / maxValue) * 100;

  return (
    <div className="bg-background text-text-primary">
      <main className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2">Territory Performance</h1>
            <p className="text-gray-400">
              FY26 {granularity === 'monthly' ? 'Monthly' : 'Quarterly'} Overview - All Territories
            </p>
          </div>
          <QuarterlyMonthlyToggle value={granularity} onChange={setGranularity} />
        </div>

        {/* Monthly cards (only when toggled) */}
        {granularity === 'monthly' && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">FY26 Monthly Performance</h2>
            {(() => {
              const monthly = data.monthlyPerformance || [];
              const hasData = monthly.some((m) => m.budget > 0 || m.actual > 0 || m.forecast > 0);
              if (!hasData) {
                return (
                  <div className="bg-black border border-white rounded-lg p-6 text-center">
                    <p className="text-sm text-gray-400">No monthly data available yet.</p>
                  </div>
                );
              }
              const sorted = [...monthly].sort((a, b) => FISCAL_MONTH_ORDER.indexOf(a.month) - FISCAL_MONTH_ORDER.indexOf(b.month));
              return <MonthlyBarChart data={sorted} />;
            })()}
          </section>
        )}

        {/* Full Year Summary - Horizontal Bar Chart */}
        <section className="mb-12">
          <div className="bg-black border-2 border-green-500 rounded-lg p-6">
            <h2 className="text-xl font-bold text-green-500 mb-6 text-center">FY26 Full Year Summary</h2>
            <div className="space-y-6">
              {/* Budget Bar */}
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-blue-400 text-sm font-semibold">Budget</span>
                  <span className="text-white text-sm font-bold">{formatCurrency(totalBudget)}</span>
                </div>
                <div className="h-10 bg-gray-800 rounded overflow-hidden">
                  <div
                    className="h-full bg-blue-500 rounded transition-all duration-500"
                    style={{ width: `${budgetWidth}%` }}
                  />
                </div>
              </div>

              {/* Actual + Forecast Stacked Bar */}
              <div>
                <div className="flex justify-between mb-2">
                  <div className="flex items-center gap-4">
                    <span className="text-green-400 text-sm font-semibold">Actual</span>
                    <span className="text-white text-sm">{formatCurrency(totalActual)}</span>
                    <span className="text-gray-500">+</span>
                    <span className="text-orange-400 text-sm font-semibold">Forecast</span>
                    <span className="text-white text-sm">{formatCurrency(totalForecast)}</span>
                  </div>
                  <span className="text-white text-sm font-bold">= {formatCurrency(totalProgress)}</span>
                </div>
                <div className="h-10 bg-gray-800 rounded overflow-hidden flex">
                  <div
                    className="h-full bg-green-500 transition-all duration-500"
                    style={{ width: `${actualWidth}%` }}
                  />
                  <div
                    className="h-full bg-orange-500 transition-all duration-500"
                    style={{ width: `${forecastWidth}%` }}
                  />
                </div>
              </div>

              {/* Status indicator */}
              <div className="flex justify-center">
                <span className={`inline-block px-4 py-2 rounded text-white text-sm font-bold ${
                  isOnTrack ? 'bg-green-500' : 'bg-orange-500'
                }`}>
                  {isOnTrack ? 'On Track' : 'Below Target'} ({Math.round((totalProgress / totalBudget) * 100)}% of Budget)
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Summary Stats */}
        <section className="mb-12">
          <div className="grid grid-cols-4 gap-6">
            <div className="bg-black border border-white rounded-lg p-6 text-center">
              <p className="text-gray-400 text-sm uppercase mb-2">Total Budget</p>
              <p className="text-blue-400 text-2xl font-bold">{formatCurrency(totalBudget)}</p>
            </div>
            <div className="bg-black border border-white rounded-lg p-6 text-center">
              <p className="text-gray-400 text-sm uppercase mb-2">Total Actual</p>
              <p className="text-green-400 text-2xl font-bold">{formatCurrency(totalActual)}</p>
            </div>
            <div className="bg-black border border-white rounded-lg p-6 text-center">
              <p className="text-gray-400 text-sm uppercase mb-2">Total Forecast</p>
              <p className="text-orange-400 text-2xl font-bold">{formatCurrency(totalForecast)}</p>
            </div>
            <div className="bg-black border border-white rounded-lg p-6 text-center">
              <p className="text-gray-400 text-sm uppercase mb-2">Territories</p>
              <p className="text-white text-2xl font-bold">{data.summary.territoryCount}</p>
            </div>
          </div>
        </section>

        {/* Territory Breakdown — granularity-aware */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Territory Breakdown</h2>
          <div className="bg-black border border-white rounded-lg overflow-x-auto">
            {granularity === 'monthly' ? (() => {
              // Monthly: 12 month columns, color-coded by performance vs budget
              const getMonthData = (t: Territory, m: number): MonthData | undefined => {
                const all = [...(t.q1Months || []), ...(t.q2Months || []), ...(t.q3Months || []), ...(t.q4Months || [])];
                return all.find((x) => x.month === m);
              };
              const getProgress = (md: MonthData): number => {
                const status = getMonthStatus(md.month);
                return status === 'past' ? md.actual : status === 'future' ? md.forecast : md.actual + md.forecast;
              };
              return (
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-4 py-3 text-left text-white font-semibold sticky left-0 bg-gray-900">Territory</th>
                      {FISCAL_MONTH_ORDER.map((m) => (
                        <th key={m} className="px-2 py-3 text-right text-xs text-gray-400 font-medium">{getMonthLabel(m)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.territories.map((t) => (
                      <tr key={t.id} className="border-t border-gray-800 hover:bg-gray-900 transition-colors">
                        <td className="px-4 py-3 text-white font-semibold sticky left-0 bg-black">{t.name}</td>
                        {FISCAL_MONTH_ORDER.map((m) => {
                          const md = getMonthData(t, m);
                          if (!md || (!md.budget && !md.actual && !md.forecast)) {
                            return <td key={m} className="px-2 py-3 text-right"><span className="text-xs text-gray-600">—</span></td>;
                          }
                          const v = getProgress(md);
                          const onTrack = v >= md.budget;
                          return (
                            <td key={m} className="px-2 py-3 text-right">
                              <span className={`text-xs font-medium ${onTrack ? 'text-green-400' : 'text-red-400'}`}>
                                {formatCurrency(v)}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-900 border-t-2 border-gray-600">
                    <tr>
                      <td className="px-4 py-3 text-white font-bold sticky left-0 bg-gray-900">TOTALS</td>
                      {FISCAL_MONTH_ORDER.map((m) => {
                        const totals = data.territories.reduce(
                          (acc, t) => {
                            const md = getMonthData(t, m);
                            if (!md) return acc;
                            return { budget: acc.budget + md.budget, progress: acc.progress + getProgress(md) };
                          },
                          { budget: 0, progress: 0 }
                        );
                        if (!totals.budget && !totals.progress) {
                          return <td key={m} className="px-2 py-3 text-right"><span className="text-xs text-gray-600">—</span></td>;
                        }
                        const onTrack = totals.progress >= totals.budget;
                        return (
                          <td key={m} className="px-2 py-3 text-right">
                            <span className={`text-xs font-bold ${onTrack ? 'text-green-400' : 'text-red-400'}`}>
                              {formatCurrency(totals.progress)}
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              );
            })() : (() => {
              // Quarterly: budget + forecast + actual per quarter, color-coded by performance vs budget
              const currentQ = getCurrentQuarterIndex();
              const getProgress = (q: QuarterData, qIdx: number): number => {
                const status = getQuarterStatus(qIdx, currentQ);
                return status === 'past' ? q.actual : status === 'future' ? q.forecast : q.actual + q.forecast;
              };
              const getColor = (q: QuarterData, qIdx: number): string => {
                const v = getProgress(q, qIdx);
                return v >= q.budget ? 'text-green-400' : 'text-red-400';
              };
              return (
                <table className="w-full">
                  <thead className="bg-gray-900">
                    <tr>
                      <th className="px-6 py-4 text-left text-white font-semibold">Territory</th>
                      <th colSpan={3} className="px-4 py-4 text-center text-white font-semibold border-l border-gray-700">Q1</th>
                      <th colSpan={3} className="px-4 py-4 text-center text-white font-semibold border-l border-gray-700">Q2</th>
                      <th colSpan={3} className="px-4 py-4 text-center text-white font-semibold border-l border-gray-700">Q3</th>
                      <th colSpan={3} className="px-4 py-4 text-center text-white font-semibold border-l border-gray-700">Q4</th>
                    </tr>
                    <tr>
                      <th className="px-6 py-3"></th>
                      {[0, 1, 2, 3].map((i) => (
                        <React.Fragment key={i}>
                          <th className="px-3 py-3 text-center text-blue-400 font-medium text-xs border-l border-gray-700">Budget</th>
                          <th className="px-3 py-3 text-center text-orange-400 font-medium text-xs">Forecast</th>
                          <th className="px-3 py-3 text-center text-green-400 font-medium text-xs">Actual</th>
                        </React.Fragment>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.territories.map((territory) => {
                      const qs = [territory.q1, territory.q2, territory.q3, territory.q4];
                      return (
                        <tr key={territory.id} className="border-t border-gray-800 hover:bg-gray-900 transition-colors">
                          <td className="px-6 py-4 text-white font-semibold">{territory.name}</td>
                          {qs.map((q, i) => (
                            <React.Fragment key={i}>
                              <td className="px-3 py-4 text-center text-white border-l border-gray-800">{formatCurrency(q.budget)}</td>
                              <td className={`px-3 py-4 text-center ${getColor(q, i)}`}>{q.forecast > 0 ? formatCurrency(q.forecast) : '-'}</td>
                              <td className={`px-3 py-4 text-center ${getColor(q, i)}`}>{q.actual > 0 ? formatCurrency(q.actual) : '-'}</td>
                            </React.Fragment>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-gray-900 border-t-2 border-gray-600">
                    <tr>
                      <td className="px-6 py-4 text-white font-bold">TOTALS</td>
                      {[0, 1, 2, 3].map((i) => {
                        const qkey = (['q1', 'q2', 'q3', 'q4'] as const)[i];
                        const totalQ = data.territories.reduce(
                          (acc, t) => ({
                            budget: acc.budget + t[qkey].budget,
                            forecast: acc.forecast + t[qkey].forecast,
                            actual: acc.actual + t[qkey].actual,
                          }),
                          { budget: 0, forecast: 0, actual: 0 }
                        );
                        const color = getColor(totalQ, i);
                        return (
                          <React.Fragment key={i}>
                            <td className="px-3 py-4 text-center text-blue-400 font-bold border-l border-gray-800">{formatCurrency(totalQ.budget)}</td>
                            <td className={`px-3 py-4 text-center font-bold ${color}`}>{formatCurrency(totalQ.forecast)}</td>
                            <td className={`px-3 py-4 text-center font-bold ${color}`}>{formatCurrency(totalQ.actual)}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              );
            })()}
          </div>
        </section>
      </main>
    </div>
  );
};
